import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Upload, Phone, Check, X, Images, Bell, Trash2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";

export default function MyProfile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ bio: "", skills: "", hourly_rate: "", city: "", phone: "", avatar_url: "", portfolio: [] });
  const [saving, setSaving] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searches, setSearches] = useState([]);
  const [newSearch, setNewSearch] = useState({ category: "any", location_label: "", latitude: null, longitude: null, radius_miles: [10] });

  useEffect(() => {
    if (!user) return;
    setForm({
      bio: user.bio || "",
      skills: (user.skills || []).join(", "),
      hourly_rate: user.hourly_rate || "",
      city: user.city || "",
      phone: user.phone || "",
      avatar_url: user.avatar_url || "",
      portfolio: user.portfolio || [],
    });
    if (user.role === "laborer") {
      api.get("/categories").then(({ data }) => setCategories(data));
      api.get("/saved-searches").then(({ data }) => setSearches(data));
    }
  }, [user]);

  if (!user) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        bio: form.bio,
        skills: form.skills.split(",").map(s => s.trim()).filter(Boolean),
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
        city: form.city,
        avatar_url: form.avatar_url,
        portfolio: form.portfolio,
      };
      const { data } = await api.put("/laborers/me", payload);
      setUser(data);
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.file_id}`;
      setForm((f) => ({ ...f, avatar_url: url }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const onAddPortfolio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPortfolio(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.file_id}`;
      const next = [...(form.portfolio || []), url].slice(0, 12);
      // Auto-persist portfolio changes
      setForm((f) => ({ ...f, portfolio: next }));
      const { data: updated } = await api.put("/laborers/me", { portfolio: next });
      setUser(updated);
      toast.success("Added to portfolio");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingPortfolio(false); e.target.value = ""; }
  };

  const removePortfolio = async (url) => {
    const next = (form.portfolio || []).filter((u) => u !== url);
    setForm((f) => ({ ...f, portfolio: next }));
    try {
      const { data: updated } = await api.put("/laborers/me", { portfolio: next });
      setUser(updated);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const useMyLocationForSearch = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => setNewSearch((s) => ({ ...s, latitude: pos.coords.latitude, longitude: pos.coords.longitude, location_label: s.location_label || "My current location" })),
      () => toast.error("Could not get location"),
    );
  };

  const createSearch = async () => {
    if (!newSearch.latitude) return toast.error("Set a location first");
    try {
      const { data } = await api.post("/saved-searches", {
        category: newSearch.category,
        latitude: newSearch.latitude,
        longitude: newSearch.longitude,
        location_label: newSearch.location_label || null,
        radius_miles: newSearch.radius_miles[0],
      });
      setSearches((prev) => [data, ...prev]);
      setNewSearch({ category: "any", location_label: "", latitude: null, longitude: null, radius_miles: [10] });
      toast.success("Saved — we'll text you when a matching task drops");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteSearch = async (id) => {
    try {
      await api.delete(`/saved-searches/${id}`);
      setSearches((prev) => prev.filter((s) => s.search_id !== id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const catLabel = (key) => key === "any" ? "Any category" : (categories.find((c) => c.key === key)?.label || key);

  const sendOtp = async () => {
    try {
      const { data } = await api.post("/phone/send-otp", { phone: form.phone });
      setOtpSent(true);
      toast.success(data.mode === "mock" ? "Mock OTP sent — check backend logs" : "OTP sent");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const verifyOtp = async () => {
    try {
      await api.post("/phone/verify-otp", { phone: form.phone, code: otp });
      toast.success("Phone verified!");
      setOtpSent(false); setOtp("");
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">My profile</h1>

      <div className="bg-white border border-slate-200/80 rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={form.avatar_url || undefined} />
            <AvatarFallback className="bg-[#3E2A4E] text-[#D9FF3D] text-xl font-bold">{user.name?.[0]}</AvatarFallback>
          </Avatar>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 hover:bg-slate-50 cursor-pointer text-sm font-semibold">
            <Upload size={14} /> Upload photo
            <input type="file" accept="image/*" onChange={onUpload} data-testid="avatar-upload-input" className="hidden" />
          </label>
        </div>

        {user.role === "laborer" && (
          <>
            <div>
              <Label htmlFor="bio">Bio</Label>
              <Textarea id="bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} className="mt-1.5" data-testid="profile-bio-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="skills">Skills (comma-separated)</Label>
                <Input id="skills" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="mt-1.5 h-11" data-testid="profile-skills-input" />
              </div>
              <div>
                <Label htmlFor="rate">Hourly rate ($)</Label>
                <Input id="rate" type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="mt-1.5 h-11" data-testid="profile-rate-input" />
              </div>
            </div>
          </>
        )}

        <div>
          <Label htmlFor="city">City</Label>
          <Input id="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 h-11" data-testid="profile-city-input" />
        </div>

        {user.role === "laborer" && (
          <Button onClick={save} disabled={saving} data-testid="profile-save-btn" className="bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold rounded-full">
            {saving ? "Saving…" : "Save profile"}
          </Button>
        )}
      </div>

      {user.role === "laborer" && (
        <div className="bg-white border border-slate-200/80 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-heading font-bold flex items-center gap-2"><Images size={16} /> Work portfolio</div>
              <div className="text-xs text-slate-500 mt-0.5">Show past jobs so clients trust you faster. Up to 12 photos.</div>
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 hover:bg-slate-50 cursor-pointer text-sm font-semibold">
              <Upload size={14} /> {uploadingPortfolio ? "Uploading…" : "Add photo"}
              <input type="file" accept="image/*" onChange={onAddPortfolio} data-testid="portfolio-upload-input" className="hidden" disabled={(form.portfolio || []).length >= 12} />
            </label>
          </div>
          {(form.portfolio || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-xl">No portfolio photos yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {form.portfolio.map((url, i) => (
                <div key={url} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 group" data-testid={`portfolio-item-${i}`}>
                  <img src={url} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePortfolio(url)}
                    data-testid={`portfolio-remove-${i}`}
                    className="absolute top-1.5 right-1.5 bg-white/95 rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200/80 rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading font-bold flex items-center gap-2">
              <Phone size={16} /> Phone verification
              {user.phone_verified && <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold"><Check size={12} /> Verified</span>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Verify your number to receive SMS updates.</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input placeholder="+15551234567" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 sm:col-span-2" data-testid="phone-input" />
          <Button onClick={sendOtp} disabled={!form.phone} data-testid="send-otp-btn" variant="outline" className="rounded-full">Send code</Button>
        </div>
        {otpSent && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)} className="h-11 sm:col-span-2" data-testid="otp-input" />
            <Button onClick={verifyOtp} data-testid="verify-otp-btn" className="bg-[#3E2A4E] hover:bg-[#2E1F3B] text-[#F3EFFA] rounded-full">Verify</Button>
          </div>
        )}
      </div>

      {user.role === "laborer" && (
        <div className="bg-white border border-slate-200/80 rounded-2xl p-6 space-y-4">
          <div>
            <div className="font-heading font-bold flex items-center gap-2"><Bell size={16} /> Job alerts (saved searches)</div>
            <div className="text-xs text-slate-500 mt-0.5">We'll text you the moment a matching task drops. Requires a verified phone.</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={newSearch.category} onValueChange={(v) => setNewSearch({ ...newSearch, category: v })}>
                <SelectTrigger className="h-11 mt-1.5" data-testid="search-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any category</SelectItem>
                  {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Center location</Label>
              <div className="flex gap-2 mt-1.5">
                <div className="flex-1">
                  <AddressAutocomplete
                    value={newSearch.location_label}
                    onChange={(v) => setNewSearch((s) => ({ ...s, location_label: v }))}
                    onSelect={({ display_name, latitude, longitude }) => setNewSearch((s) => ({ ...s, location_label: display_name, latitude, longitude }))}
                    placeholder="Address or area…"
                    testid="search-location-input"
                  />
                </div>
                <Button type="button" variant="outline" onClick={useMyLocationForSearch} data-testid="search-use-location-btn" className="rounded-full h-11 whitespace-nowrap">
                  {newSearch.latitude ? "Pinned" : "Pin me"}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">Radius</div>
            <Slider value={newSearch.radius_miles} onValueChange={(v) => setNewSearch({ ...newSearch, radius_miles: v })} min={1} max={100} step={1} className="flex-1" data-testid="search-radius-slider" />
            <div className="font-mono-money text-sm text-slate-900 w-16 text-right">{newSearch.radius_miles[0]} mi</div>
          </div>

          <Button onClick={createSearch} disabled={!newSearch.latitude} data-testid="save-search-btn" className="bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold rounded-full">
            <Bell size={14} className="mr-1" /> Save alert
          </Button>

          {searches.length > 0 && (
            <div className="pt-2 space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Your alerts</div>
              {searches.map((s) => (
                <div key={s.search_id} data-testid={`saved-search-${s.search_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-sm">{catLabel(s.category)} · within {s.radius_miles} mi</div>
                    <div className="text-xs text-slate-500 truncate">{s.location_label || `${s.latitude.toFixed(3)}, ${s.longitude.toFixed(3)}`}</div>
                  </div>
                  <button onClick={() => deleteSearch(s.search_id)} data-testid={`delete-search-${s.search_id}`} className="p-2 rounded-full hover:bg-slate-200 text-slate-500">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
